import {
  bindRealPersonGroup,
  createRealPersonSession,
  getRealPersonSessionResult,
  listRealPersonGroups,
  type RealPersonRequestOptions,
} from '../api/realPerson';
import { getApiBusinessFailureMessage } from './seedanceRequestUtils';
import { pickStringKeyIgnoreCase } from './seedancePortraitUtils';

const REAL_PORTRAIT_POLL_INTERVAL_MS = 2500;
const REAL_PORTRAIT_POLL_MAX_MS = 120_000;

const CALLBACK_QUERY_KEYS = [
  'bytedToken',
  'resultCode',
  'algorithmBaseRespCode',
  'reqMeasureInfoValue',
  'verify_type',
] as const;

export type RealPortraitCallbackParams = {
  bytedToken: string;
  resultCode: string;
};

/** HashRouter 下生成不含旧回调参数的 callback_url */
export const buildRealPortraitCallbackUrl = (): string => {
  const { origin, pathname, hash } = window.location;
  const hashPath = (hash.split('?')[0] || '#/library').trim();
  return `${origin}${pathname}${hashPath}`;
};

export const parseRealPortraitCallbackParams = (): RealPortraitCallbackParams => {
  const fromSearch = new URLSearchParams(window.location.search);
  const hashQuery = window.location.hash.includes('?')
    ? window.location.hash.slice(window.location.hash.indexOf('?') + 1)
    : '';
  const fromHash = new URLSearchParams(hashQuery);
  const pick = (key: string) => (fromHash.get(key) || fromSearch.get(key) || '').trim();
  return {
    bytedToken: pick('bytedToken'),
    resultCode: pick('resultCode'),
  };
};

export const stripRealPortraitCallbackParams = () => {
  const url = new URL(window.location.href);
  CALLBACK_QUERY_KEYS.forEach((key) => url.searchParams.delete(key));

  const [hashPath, hashQuery = ''] = window.location.hash.split('?');
  const hashParams = new URLSearchParams(hashQuery);
  CALLBACK_QUERY_KEYS.forEach((key) => hashParams.delete(key));
  const nextHashQuery = hashParams.toString();
  const nextHash = nextHashQuery ? `${hashPath}?${nextHashQuery}` : hashPath;

  const nextUrl = `${url.origin}${url.pathname}${url.search}${nextHash}`;
  window.history.replaceState(null, '', nextUrl);
};

export const pickRealPortraitSessionFields = (
  res: unknown,
): { bytedToken: string; h5Link: string } => {
  const root = res && typeof res === 'object' ? (res as Record<string, unknown>) : {};
  const data = root.Result || root.data || root;
  const dataObj = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  return {
    bytedToken: pickStringKeyIgnoreCase(dataObj, 'bytedtoken'),
    h5Link: pickStringKeyIgnoreCase(dataObj, 'h5link'),
  };
};

export const pickRealPortraitGroupId = (res: unknown): string => {
  const root = res && typeof res === 'object' ? (res as Record<string, unknown>) : {};
  const data = root.Result || root.data || root;
  const dataObj = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  return pickStringKeyIgnoreCase(dataObj, 'groupid') || pickStringKeyIgnoreCase(root, 'groupid');
};

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timer = window.setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      window.clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });

export async function startRealPortraitAuthSession(
  options: RealPersonRequestOptions,
): Promise<{ bytedToken: string; h5Link: string }> {
  const callback_url = buildRealPortraitCallbackUrl();
  options.onDebug?.(`真人认证 callback_url=${callback_url}`);
  const res = await createRealPersonSession({ callback_url }, options);
  const bizErr = getApiBusinessFailureMessage(res);
  if (bizErr) throw new Error(bizErr);
  const { bytedToken, h5Link } = pickRealPortraitSessionFields(res);
  if (!bytedToken || !h5Link) {
    throw new Error('创建认证会话失败：未返回 BytedToken 或 H5Link');
  }
  return { bytedToken, h5Link };
}

export async function pollRealPortraitGroupId(
  bytedToken: string,
  options: RealPersonRequestOptions,
  onStatus?: (message: string) => void,
  signal?: AbortSignal,
): Promise<string> {
  const token = bytedToken.trim();
  if (!token) throw new Error('缺少 byted_token');

  const deadline = Date.now() + REAL_PORTRAIT_POLL_MAX_MS;
  while (Date.now() < deadline) {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

    const res = await getRealPersonSessionResult({ byted_token: token }, options);
    const bizErr = getApiBusinessFailureMessage(res);
    if (bizErr) throw new Error(bizErr);

    const groupId = pickRealPortraitGroupId(res);
    if (groupId) return groupId;

    onStatus?.('等待认证结果，请在 H5 页面完成真人认证…');
    await sleep(REAL_PORTRAIT_POLL_INTERVAL_MS, signal);
  }

  throw new Error('查询认证结果超时，请重试');
}

export const pickRealPersonBoundGroupIds = (res: unknown): string[] => {
  const root = res && typeof res === 'object' ? (res as Record<string, unknown>) : {};
  const data = root.Result || root.data || root;
  const dataObj = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};

  if (Array.isArray(dataObj.Items)) {
    return dataObj.Items
      .filter((item): item is Record<string, unknown> => item && typeof item === 'object')
      .map((item) => (item.Id || item.id) as string)
      .filter((id) => typeof id === 'string' && id.trim().length > 0)
      .map((id) => id.trim());
  }

  const raw =
    dataObj.real_person_asset_group_ids ??
    dataObj.realPersonAssetGroupIds ??
    root.real_person_asset_group_ids;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
    .map((id) => id.trim());
};

/** POST /api/real_person/group — 将认证得到的 GroupId 绑定到当前用户 */
export async function bindRealPersonGroupForUser(
  groupId: string,
  options: RealPersonRequestOptions,
): Promise<void> {
  const id = groupId.trim();
  if (!id) throw new Error('缺少 group_id');
  const res = await bindRealPersonGroup({ group_id: id }, options);
  const bizErr = getApiBusinessFailureMessage(res);
  if (bizErr) throw new Error(bizErr);
}

/** GET /api/real_person/group — 查询已绑定的真人素材组 ID 列表 */
export async function fetchRealPersonBoundGroupIds(
  options: RealPersonRequestOptions,
): Promise<string[]> {
  const res = await listRealPersonGroups(options);
  const bizErr = getApiBusinessFailureMessage(res);
  if (bizErr) throw new Error(bizErr);
  return pickRealPersonBoundGroupIds(res);
}
